import { noop } from '@sgme/fp';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';

import { GroupColoredIcon } from '../colorSelector/GroupColoredIcon';
import type { FilterToIconMapper } from './Filter.helper';
import { GroupMenu } from './GroupMenu';

export type FilterFormatter<T extends string> = (filter: T) => string;

interface FilterItemProps<T extends string> {
  filter: T;
  isActive: boolean;
  count: number;
  e2e: string;
  label?: string | FilterFormatter<T>;
  icon?: string | FilterToIconMapper<T>;
  selectedColor?: GroupColor;
  onSelected(group: string): void;
  onTilesDeleted(group: string): void;
  onRenamed?: (previousGroupName: string, newGroupName: string) => void;
  onColorSelected?: (groupId: string, color?: GroupColor) => void;
}

export const FilterItem = <T extends string>({
  filter,
  isActive,
  count,
  e2e,
  icon,
  selectedColor,
  label,
  onSelected,
  onTilesDeleted,
  onRenamed,
  onColorSelected,
}: FilterItemProps<T>) => {
  const onFilterClicked = useCallback(() => (isActive ? noop : onSelected(filter)), [filter, onSelected, isActive]);
  const onDeleteFilterTilesClicked = useCallback(() => onTilesDeleted(filter), [filter, onTilesDeleted]);

  const itemIcon: string = useMemo(
    () => (icon ? (typeof icon === 'function' ? icon(filter) : icon.toString()) : 'folder'),
    [filter, icon],
  );

  const displayedLabel: string = useMemo(
    () => (label ? (typeof label === 'function' ? label(filter) : label.toString()) : filter),
    [filter, label],
  );

  return (
    <li className="nav-item">
      <span
        className={classNames('nav-link d-flex flex-row align-items-center', {
          active: isActive,
          clickable: !isActive,
        })}
        onClick={onFilterClicked}
        data-e2e={`${e2e}-${filter}`}
      >
        <GroupColoredIcon iconName={itemIcon} color={selectedColor} />
        <span className="ms-2 flex-grow-1">{displayedLabel}</span>
        <span className="badge rounded-pill badge-primary monospace-numbers" data-e2e={`${e2e}-${filter}-count`}>
          {count}
        </span>
        <GroupMenu
          groupId={filter}
          selectedColor={selectedColor}
          onMenuToggled={onFilterClicked}
          onDeleted={onDeleteFilterTilesClicked}
          onRenamed={onRenamed}
          onColorSelected={onColorSelected}
        />
      </span>
    </li>
  );
};
FilterItem.displayName = 'FilterItem';
